import type { Playlist, Track } from "../model";
import { playlistTracks } from "../store/selectors";
import { createStorage } from "./storage";

const MANIFEST_FILE = ".needle-drop-sync.json";
const PLAYLISTS_FOLDER = "Playlists";

interface ManagedFile { name: string; size: number; fileHash: string; }
interface ManagedPlaylist { name: string; files: Record<string, ManagedFile>; }
interface SyncManifest {
  version: 2;
  generatedAt: string;
  files: Record<string, ManagedFile>;
  playlistFolders: Record<string, ManagedPlaylist>;
}

type LegacyManifest = Omit<SyncManifest, "version" | "playlistFolders"> & { version: 1 };
type SyncResult = { written: number; removed: number };
let syncQueue: Promise<SyncResult> = Promise.resolve({ written: 0, removed: 0 });

declare global {
  interface Window {
    showDirectoryPicker?: (options?: { id?: string; mode?: "read" | "readwrite" }) => Promise<FileSystemDirectoryHandle>;
  }
  interface FileSystemHandle {
    queryPermission(options?: { mode?: "read" | "readwrite" }): Promise<PermissionState>;
    requestPermission(options?: { mode?: "read" | "readwrite" }): Promise<PermissionState>;
  }
}

function safeFileName(track: Track) {
  const fallback = `${track.artist ? `${track.artist} - ` : ""}${track.title || track.id}.mp3`;
  const source = track.fileName.toLowerCase().endsWith(".mp3") ? track.fileName : fallback;
  return source.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_").slice(0, 240);
}

function safeFolderName(value: string) {
  return value.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_").replace(/[. ]+$/g, "").trim().slice(0, 120) || "Untitled Playlist";
}

function withTrackSuffix(name: string, trackId: string) {
  return name.replace(/\.mp3$/i, ` [${trackId.slice(0, 8)}].mp3`);
}

function desiredFiles(tracks: Track[]) {
  const nameCounts = new Map<string, number>();
  for (const track of tracks) {
    const name = safeFileName(track).toLocaleLowerCase();
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }
  return new Map(tracks.map((track) => {
    const baseName = safeFileName(track);
    const name = (nameCounts.get(baseName.toLocaleLowerCase()) ?? 0) > 1 ? withTrackSuffix(baseName, track.id) : baseName;
    return [track.id, { track, name }] as const;
  }));
}

function desiredPlaylists(tracks: Track[], playlists: Playlist[]) {
  const nameCounts = new Map<string, number>();
  for (const playlist of playlists) {
    const name = safeFolderName(playlist.name).toLocaleLowerCase();
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }
  return new Map(playlists.map((playlist) => {
    const baseName = safeFolderName(playlist.name);
    const name = (nameCounts.get(baseName.toLocaleLowerCase()) ?? 0) > 1 ? `${baseName} [${playlist.id.slice(0, 8)}]` : baseName;
    return [playlist.id, { name, files: desiredFiles(playlistTracks(tracks, playlist)) }] as const;
  }));
}

async function readManifest(handle: FileSystemDirectoryHandle): Promise<SyncManifest | undefined> {
  try {
    const file = await (await handle.getFileHandle(MANIFEST_FILE)).getFile();
    const parsed = JSON.parse(await file.text()) as Partial<SyncManifest | LegacyManifest>;
    if (!parsed.files || typeof parsed.files !== "object") return undefined;
    if (parsed.version === 1) return { ...parsed, version: 2, playlistFolders: {} } as SyncManifest;
    if (parsed.version !== 2 || !parsed.playlistFolders || typeof parsed.playlistFolders !== "object") return undefined;
    return parsed as SyncManifest;
  } catch { return undefined; }
}

async function writeManifest(handle: FileSystemDirectoryHandle, files: SyncManifest["files"], playlistFolders: SyncManifest["playlistFolders"]) {
  const fileHandle = await handle.getFileHandle(MANIFEST_FILE, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(JSON.stringify({ version: 2, generatedAt: new Date().toISOString(), files, playlistFolders } satisfies SyncManifest, null, 2));
  await writable.close();
}

async function removeManagedFile(handle: FileSystemDirectoryHandle, name: string) {
  if (!name || name === MANIFEST_FILE || name.includes("/") || name.includes("\\")) return false;
  try { await handle.removeEntry(name); return true; }
  catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") return false;
    throw error;
  }
}

async function getExistingDirectory(handle: FileSystemDirectoryHandle, name: string) {
  try { return await handle.getDirectoryHandle(name); }
  catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") return undefined;
    throw error;
  }
}

async function removeManagedPlaylistFolder(playlistsHandle: FileSystemDirectoryHandle, playlist: ManagedPlaylist) {
  const folder = await getExistingDirectory(playlistsHandle, playlist.name);
  if (!folder) return 0;
  let removed = 0;
  for (const file of Object.values(playlist.files)) if (await removeManagedFile(folder, file.name)) removed += 1;
  try { await playlistsHandle.removeEntry(playlist.name); }
  catch (error) {
    if (!(error instanceof DOMException) || !["NotFoundError", "InvalidModificationError", "NoModificationAllowedError"].includes(error.name)) throw error;
  }
  return removed;
}

export async function chooseSyncFolder() {
  if (!window.showDirectoryPicker) {
    if ("brave" in navigator) throw new Error("Brave blocks folder access by default. Open brave://flags/#file-system-access-api, set File System Access API to Enabled, then relaunch Brave.");
    if (navigator.userAgent.includes("OPR/")) throw new Error("This Opera version does not expose folder access. Update Opera and try again in a normal, non-private window.");
    throw new Error("Folder sync requires a desktop browser with File System Access support, such as Chrome, Edge or Opera.");
  }
  return window.showDirectoryPicker({ id: "needle-drop-library", mode: "readwrite" });
}

export function folderSyncSupportMessage() {
  if (window.showDirectoryPicker) return "Folder access is available in this browser.";
  if ("brave" in navigator) return "BRAVE SETUP REQUIRED: Open brave://flags/#file-system-access-api, change File System Access API to Enabled, and relaunch Brave. Then return here and click LINK FOLDER again.";
  if (navigator.userAgent.includes("OPR/")) return "Update Opera and use a normal, non-private window. This Opera installation currently hides the required folder API.";
  return "Use an up-to-date desktop version of Chrome, Edge or Opera. Safari and Firefox cannot maintain a writable folder mirror.";
}

async function syncPlaylistFiles(rootHandle: FileSystemDirectoryHandle, rootFiles: ReturnType<typeof desiredFiles>, playlistHandle: FileSystemDirectoryHandle, desired: ReturnType<typeof desiredFiles>, previous?: ManagedPlaylist) {
  let written = 0;
  let removed = 0;
  for (const [trackId, entry] of desired) {
    const fileHandle = await playlistHandle.getFileHandle(entry.name, { create: true });
    const existing = await fileHandle.getFile();
    if (existing.size !== entry.track.fileSize) {
      const rootName = rootFiles.get(trackId)?.name;
      if (!rootName) continue;
      const rootFile = await (await rootHandle.getFileHandle(rootName)).getFile();
      const writable = await fileHandle.createWritable();
      await writable.write(rootFile);
      await writable.close();
      written += 1;
    }
    const oldName = previous?.files[trackId]?.name;
    if (oldName && oldName !== entry.name && await removeManagedFile(playlistHandle, oldName)) removed += 1;
  }
  if (previous) {
    for (const [trackId, oldEntry] of Object.entries(previous.files)) {
      if (!desired.has(trackId) && await removeManagedFile(playlistHandle, oldEntry.name)) removed += 1;
    }
  }
  return { written, removed };
}

async function performFolderSync(handle: FileSystemDirectoryHandle, tracks: Track[], playlists: Playlist[], apiBaseUrl: string, requestPermission: boolean): Promise<SyncResult> {
  let permission = await handle.queryPermission({ mode: "readwrite" });
  if (permission !== "granted" && requestPermission) permission = await handle.requestPermission({ mode: "readwrite" });
  if (permission !== "granted") throw new Error("Open Preferences and allow folder access to continue syncing.");
  const previous = await readManifest(handle);
  const desired = desiredFiles(tracks);
  const playlistFolders = desiredPlaylists(tracks, playlists);
  let written = 0;
  let removed = 0;

  for (const [trackId, entry] of desired) {
    const fileHandle = await handle.getFileHandle(entry.name, { create: true });
    const existing = await fileHandle.getFile();
    if (existing.size !== entry.track.fileSize) {
      const blob = await createStorage("r2", apiBaseUrl).download(trackId);
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      written += 1;
    }
    const oldName = previous?.files[trackId]?.name;
    if (oldName && oldName !== entry.name && await removeManagedFile(handle, oldName)) removed += 1;
  }
  if (previous) {
    for (const [trackId, oldEntry] of Object.entries(previous.files)) {
      if (!desired.has(trackId) && await removeManagedFile(handle, oldEntry.name)) removed += 1;
    }
  }

  const playlistsHandle = await handle.getDirectoryHandle(PLAYLISTS_FOLDER, { create: true });
  for (const [playlistId, entry] of playlistFolders) {
    const old = previous?.playlistFolders[playlistId];
    if (old && old.name !== entry.name) removed += await removeManagedPlaylistFolder(playlistsHandle, old);
    const folder = await playlistsHandle.getDirectoryHandle(entry.name, { create: true });
    const result = await syncPlaylistFiles(handle, desired, folder, entry.files, old?.name === entry.name ? old : undefined);
    written += result.written;
    removed += result.removed;
  }
  if (previous) {
    for (const [playlistId, old] of Object.entries(previous.playlistFolders)) {
      if (!playlistFolders.has(playlistId)) removed += await removeManagedPlaylistFolder(playlistsHandle, old);
    }
  }

  await writeManifest(
    handle,
    Object.fromEntries([...desired].map(([trackId, entry]) => [trackId, { name: entry.name, size: entry.track.fileSize, fileHash: entry.track.fileHash }])),
    Object.fromEntries([...playlistFolders].map(([playlistId, entry]) => [playlistId, {
      name: entry.name,
      files: Object.fromEntries([...entry.files].map(([trackId, file]) => [trackId, { name: file.name, size: file.track.fileSize, fileHash: file.track.fileHash }])),
    }])),
  );
  return { written, removed };
}

export function syncTracksToFolder(handle: FileSystemDirectoryHandle, tracks: Track[], playlists: Playlist[], apiBaseUrl: string, requestPermission = false) {
  const next = syncQueue.then(() => performFolderSync(handle, tracks, playlists, apiBaseUrl, requestPermission));
  syncQueue = next.catch(() => ({ written: 0, removed: 0 }));
  return next;
}
